import type { ComponentProps, ReactElement } from "react";
import type { ZodRawShape } from "zod";

import { Form as UIForm, type FormElements } from "@rootreeweb/rui-forms";
import { Button, Message } from "@rootreeweb/ui";
import {
	createBlankStateFromSchema,
	type TypedFormState,
} from "@rootreeweb/zod-to-elements";

import type { FormComponentProps, FormOptions } from "../types.js";

/**
 * Handles the redirect after form submission
 *
 * @param redirect The destination.
 */
function navigateToRedirect(redirect: string) {
	window.location.href = redirect;
}

/**
 * Wrapper around the form from the UI library that handles form submission and
 * provides default behaviour for the forms in /js/forms.
 *
 * @param props The props for the form.
 * @returns The form component.
 */
const Form = <T extends ZodRawShape>(
	props: FormComponentProps<T>,
): ReactElement => {
	const { formDefinition } = props;
	const { formID, formOptions = {}, schema } = formDefinition;

	const SubmitButton = useSubmitButton(formOptions);
	const defaultState =
		props.defaultValues || createBlankStateFromSchema(schema.shape);

	if (props.formMutator.status === "pending") {
		return (
			<div id={formID}>
				<Message status={"info"}>
					{formOptions.strings?.sending || "Sending..."}
				</Message>
			</div>
		);
	}
	if (props.formMutator.status === "error") {
		return (
			<div id={formID}>
				<Message status={"error"}>
					{formOptions.strings?.error || "Error..."}
					<br />
					{JSON.stringify(props.formMutator.error || "", null, "\t")}
				</Message>
			</div>
		);
	}

	if (props.formMutator.status === "success") {
		if (formOptions.redirect && !formOptions.testing) {
			navigateToRedirect(formOptions.redirect);
		}
		if (!formOptions.allowMultipleSubmissions) {
			return (
				<div id={formID}>
					<Message status={"success"}>
						{formOptions.strings?.success || "Submitted!"}
					</Message>
				</div>
			);
		}
	}
	return (
		<div id={formID}>
			{props.formMutator.status === "success" && (
				<Message status={"success"}>
					{formOptions.strings?.success || "Submitted!"}
				</Message>
			)}
			<UIForm
				defaults={defaultState as TypedFormState<typeof schema.shape>}
				endpoint={(e) => {
					props.formMutator.mutate(e);
				}}
				schema={schema}
				{...props.UIFormProps}
				formElements={{
					...(props.UIFormProps?.formElements || {}),
					SubmitButton,
				}}
			/>
		</div>
	);
};

const useSubmitButton = (
	formOptions?: FormOptions,
): FormElements["SubmitButton"] => {
	return (props: ComponentProps<FormElements["SubmitButton"]>) => (
		<Button {...props}>
			{formOptions?.strings?.submitButton ||
				// eslint-disable-next-line @typescript-eslint/no-deprecated
				formOptions?.submitButtonLabel ||
				"Submit"}
		</Button>
	);
};
export default Form;
